import {
  Box,
  Button, Flex,
  HStack, Image,
  Input,
  Link, Spinner,
  Stack,
  Text,
  useColorMode,
  VStack
} from '@chakra-ui/react'
import { useWallet } from '@solana/wallet-adapter-react'
import { Transaction } from '@solana/web3.js'
import { Select } from 'chakra-react-select'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { HiOutlineTicket } from 'react-icons/hi'
import { useAsyncFn } from 'react-use'
import { WalletMultiButton } from '../../../components/wallet-ui'
import { connection } from '../../../config/config'
import { monetFeatureConfig } from '../../../config/monetFeatureConfig'
import {
  handleTransaction, handleTransactionError, sendTransactionWithRetry
} from '../../../utils/solUtils'
import { compareTokensForSort } from '../../../utils/tokenSortUtil'
import { trpc } from '../../../utils/trpc'
import { formatFloatForDisplay } from '../../../utils/utils'
import { useSocialLink } from '../../admin/components/discord-link-ui/discordLinkHooks'
import { DiscordLinkMultiButton } from '../../admin/components/discord-link-ui/DiscordLinkMultiButton'
import { TwitterLinkMultiButton } from '../../admin/components/twitter-link-ui/TwitterLinkMultiButton'
import { NormalRaffleEntry } from '../../rewards/components/NormalRaffleEntryModal'
import {
  getPointsForBuyingTicketsWithMultiplier,
  PointsGoLiveDate
} from '../../rewards/utils/pointsUtils'
import {
  UseOnChainRaffleType,
  UseOnChainUserRaffleType
} from '../../techRaffles/hooks/hookTypes'
import { purchaseTokenType, raffleType } from '../../techRaffles/types'
import { CharkaSelectStyle } from '../consts/styles'
import reattempt from 'reattempt'

type BuyTicketProps = {
  onChainUserRaffleRes: UseOnChainUserRaffleType
  onChainRaffleRes: UseOnChainRaffleType
  raffle: raffleType
}

const ConnectWalletBox = () => {
  return (
    <VStack align='center' justify='center' gap='.4rem'>
      <Text fontWeight={600} fontSize='0.9rem'>
        Please connect your wallet first to enter a raffle.
      </Text>
      <Stack direction={{ base: 'column', md: 'row' }} gap={3}>
        <WalletMultiButton />
      </Stack>
    </VStack>
  )
}

const ConnectDiscordBox = (props: { text: string }) => {
  return (
    <VStack align='center' justify='center' gap='.4rem'>
      <Text fontWeight={600} fontSize='0.9rem'>
        {props.text}
      </Text>
      <Stack direction={{ base: 'column', md: 'row' }} gap={3}>
        <DiscordLinkMultiButton />
      </Stack>
    </VStack>
  )
}

const HolderCheckBox = (props: {
  communityName: string
  discordInviteLink: string | null
  isLoading: boolean
}) => {
  if (props.isLoading) {
    return (
      <VStack align='center' justify='center' gap='.4rem'>
        <Text fontWeight={600} fontSize='0.9rem'>
          Holder check in progress! This can take up to 60 seconds.
        </Text>
        <Spinner />
      </VStack>
    )
  }
  return (
    <VStack align='center' justify='center' gap='.4rem'>
      <Text fontWeight={600} fontSize='0.9rem'>
        You aren't a holder of {props.communityName}.
      </Text>
      <Text fontSize='0.9rem'>
        Please check in the{' '}
        {props.discordInviteLink ? (
          <Link isExternal href={props.discordInviteLink}>
            {props.communityName} Discord
          </Link>
        ) : (
          <Text as='span'>{props.communityName} Discord</Text>
        )}{' '}
        if you have any holder roles.
      </Text>
      <Stack direction={{ base: 'column', md: 'row' }} gap={3}>
        <DiscordLinkMultiButton />
      </Stack>
    </VStack>
  )
}

export const BuyTickets: React.FC<BuyTicketProps> = ({
  raffle,
  onChainRaffleRes,
  onChainUserRaffleRes,
}) => {
  const [showPopup, setShowPopup] = useState(false)
  const wallet = useWallet()
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const [buttonLoadingText, setButtonLoadingText] = useState('Confirming...')
  const [selectedPurchaseToken, setSelectedPurchaseToken] = useState<
    purchaseTokenType | undefined
  >(undefined)
  const [ticketAmountToBuy, setTicketAmountToBuy] = useState<string>('4')
  const { discordUsername, twitterUsername } = useSocialLink()
  const { data: verifiedHolder, isLoading: isVerifiedHolderLoading } =
    trpc.useQuery(
      [
        'project.is-holder-in-project',
        {
          id: raffle.creatorProject?.id ?? '',
          wallet: wallet.publicKey?.toBase58(),
        },
      ],
      {
        enabled:
          !!raffle.creatorProject && raffle.holdersOnly && !!discordUsername,
      }
    )

  const allowedPurchaseTokens = useMemo(() => {
    if (!raffle.allowedPurchaseTokens) return []
    return raffle.allowedPurchaseTokens.sort((a, b) =>
      compareTokensForSort(a.token, b.token)
    )
  }, [raffle.allowedPurchaseTokens])

  useEffect(() => {
    if (
      selectedPurchaseToken &&
      raffle?.allowedPurchaseTokens.some(
        (purchaseToken) =>
          purchaseToken.token.symbol === selectedPurchaseToken.token.symbol
      )
    ) {
      return
    }

    if (raffle?.allowedPurchaseTokens?.length) {
      const selectedToken = raffle.allowedPurchaseTokens.find(
        (purchaseTokenObj) =>
          purchaseTokenObj.token.symbol === raffle.ticketPriceToken.symbol
      )
      const solToken = raffle.allowedPurchaseTokens.find(
        (purchaseTokenObj) => purchaseTokenObj.token.symbol === 'SOL'
      )
      if (selectedToken) {
        setSelectedPurchaseToken(selectedToken)
      } else if (solToken) {
        setSelectedPurchaseToken(solToken)
      } else {
        setSelectedPurchaseToken(raffle.allowedPurchaseTokens[0])
      }
    }
  }, [raffle])

  const previewTicketPrice = useMemo(() => {
    if (!raffle || !selectedPurchaseToken) return null
    const ticketPriceToken = raffle.ticketPriceToken
    if (selectedPurchaseToken.token.symbol === ticketPriceToken.symbol) {
      return raffle.ticketPrice
    }
    if (selectedPurchaseToken.fixedPrice) {
      return selectedPurchaseToken.fixedPrice
    }
    const previewTicketPrice =
      (raffle.ticketPrice * (ticketPriceToken.lastUsdcPrice ?? 0)) /
      (selectedPurchaseToken.token.lastUsdcPrice ?? 1)
    const discount = selectedPurchaseToken.discount ?? 0
    console.log({ discount })
    console.log({ previewTicketPrice })

    const discountedPrice = previewTicketPrice * (1 - discount / 100)
    return discountedPrice * 1.0125 // 1.0125 for slippage
  }, [raffle, selectedPurchaseToken])

  const {
    totalTicketValueInSol,
    possibleGemsEarned,
    possibleExperiencePointsEarned,
  } = useMemo(() => {
    const totalTicketValueInSol =
      (raffle.estimateTicketPriceInSol ?? 0) * parseInt(ticketAmountToBuy, 10)
    const { gems, experiencePoints } = getPointsForBuyingTicketsWithMultiplier(
      totalTicketValueInSol,
      raffle.pointsMultiplier
    )
    return {
      totalTicketValueInSol,
      possibleGemsEarned: gems,
      possibleExperiencePointsEarned: experiencePoints,
    }
  }, [
    raffle.estimateTicketPriceInSol,
    raffle.pointsMultiplier,
    ticketAmountToBuy,
  ])

  const buyRaffleTicketMut = trpc.useMutation(['raffle.buyRaffleTicket'], {
    onSuccess: async (data) => {},
    onError: (error) => {
      console.log('error buy ticket request', error)
      toast.error(error.message)
    },
  })

  const confirmBuyTicketMut = trpc.useMutation(
    ['raffle.confirmBuyRaffleTicket'],
    {
      onSuccess: async (data) => {},
      onError: (error) => {
        console.log('error buy ticket confirm for adding points', error)
      },
    }
  )

  const [buyTicketRes, buyTicket] = useAsyncFn(async () => {
    if (
      !wallet.publicKey ||
      !wallet.signTransaction ||
      !raffle ||
      !selectedPurchaseToken
    )
      return

    if (!monetFeatureConfig.ticketSale) {
      toast('Ticket sale disabled')
      return
    }

    console.log('buying ticket starting')

    try {
      setButtonLoadingText('Creating transaction...')
      const buyTicketResData = await buyRaffleTicketMut.mutateAsync({
        raffleId: raffle.id,
        payTokenSymbol: selectedPurchaseToken.token.symbol,
        ticketCount: parseInt(ticketAmountToBuy, 10),
        userWalletBase58: wallet.publicKey.toBase58(),
      })

      console.log('successful buy ticket init', buyTicketResData)
      if (
        !wallet.publicKey ||
        !wallet.signTransaction ||
        !raffle ||
        !selectedPurchaseToken
      )
        return

      const transaction = Transaction.from(
        Buffer.from((buyTicketResData as any).trans)
      )

      setButtonLoadingText('Approve in wallet...')
      const signedTx = await wallet.signTransaction(transaction)
      setButtonLoadingText('Sending on-chain...')
      const serial = signedTx.serialize({
        verifySignatures: false,
        requireAllSignatures: false,
      })


      const tx = await reattempt.run(
        { times: 6, delay: 600 },
        async () => {
          const [tx1, tx2, tx3] = await Promise.all([
            sendTransactionWithRetry(serial),
            sendTransactionWithRetry(serial),
            sendTransactionWithRetry(serial),
            sendTransactionWithRetry(serial),
            sendTransactionWithRetry(serial),
            sendTransactionWithRetry(serial),
          ])

          return tx1
        }
      )


      setButtonLoadingText('Confirming on-chain...')

      console.log('handle tx')

      await handleTransaction(tx, {
        blockhash: buyTicketResData.blockhash,
        commitment: 'processed',
        connection: connection,
        showLogs: true,
      })

      setButtonLoadingText('Fetching new data...')

      const [confirmBuyTicketRes, ...rest] = await Promise.all([
        confirmBuyTicketMut.mutateAsync({
          raffleId: raffle.id,
          userWalletBase58: wallet.publicKey.toBase58(),
          tokenId: selectedPurchaseToken.token.id,
          tokenSymbol: selectedPurchaseToken.token.symbol,
          txSignature: tx,
        }),
        onChainRaffleRes.refetch(),
        onChainUserRaffleRes.refetch(),
      ])

      console.log(confirmBuyTicketRes)

      setShowPopup(true)
    } catch (e: any) {
      console.error('Error in buying tickets', e)
      toast.error('Buying ticket failed')
      const anchorErrorMessage = handleTransactionError(e)
      if (anchorErrorMessage) {
        toast.error(anchorErrorMessage)
      } else if (e.message.includes('0x12d')) {
        toast.error('Raffle has ended')
      } else {
        toast.error(e.message)
      }
    }
  }, [wallet, selectedPurchaseToken, ticketAmountToBuy, raffle.id])

  const handleClosePopup = () => {
    setShowPopup(false)
  }

  // const PriceSelector = () => {
  //   return (

  //   )
  // }

  const pointsEarned = getPointsForBuyingTicketsWithMultiplier(
    (onChainUserRaffleRes?.data?.counter ?? 0) *
      (raffle.estimateTicketPriceInSol ?? 0),
    raffle.pointsMultiplier
  )

  return (
    <Box
      mt='1.rem'
      bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
      borderRadius='1.875rem'
      paddingY='1.5rem'
      paddingX='1.5rem'
    >
      {showPopup && (
        <NormalRaffleEntry
          showPopup={showPopup}
          handleClosePopup={() => setShowPopup(false)}
          experiencePointsEarned={
            confirmBuyTicketMut.data?.experiencePointsEarned
          }
          gemsEarned={confirmBuyTicketMut.data?.gemsEarned}
          raffleName={raffle.name}
          raffleId={raffle.id}
          collectionTwitter={raffle.collection?.twitter}
        />
      )}
      {!buyTicketRes.loading &&
        !onChainRaffleRes.isLoading &&
        (onChainUserRaffleRes?.data?.counter ?? 0) > 0 && (
          <Text
            textAlign={'center'}
            fontSize={['0.9rem', '1rem']}
            fontWeight={600}
            color={isDarkMode ? '#dbd8d8' : '#6b6b6b'}
          >
            👏 You purchased{' '}
            {buyTicketRes.loading || onChainUserRaffleRes.isLoading ? (
              <Spinner size='sm' mt='0.3rem' />
            ) : (
              onChainUserRaffleRes?.data?.counter ?? (
                <Spinner size='sm' mt='0.3rem' />
              )
            )}{' '}
            ticket{onChainUserRaffleRes?.data?.counter === 1 ? '' : 's'}
            {raffle.ends > PointsGoLiveDate ? (
              <> earning {formatFloatForDisplay(pointsEarned.gems)} GEMS 👏</>
            ) : (
              <></>
            )}
          </Text>
        )}

      {onChainRaffleRes.data?.ticketCount === raffle.maxTickets && typeof onChainRaffleRes.data?.ticketCount === 'number' ? (
        <Text
          align={'center'}
          mt='2rem'
          fontWeight={600}
          color={isDarkMode ? '#dbd8d8' : '#6b6b6b'}
        >
          Raffle sold out and will be raffled soon.
        </Text>
      ) : (
        <>
          {raffle.needsSocial && (
            <Text mb='1rem'>
              This is a special raffle for verified users, only people with a
              connected Twitter account can enter.{' '}
              {raffle.maxTicketsPerUser &&
                `Max tickets per user: ${raffle.maxTicketsPerUser}`}
            </Text>
          )}
          {!wallet.publicKey && <ConnectWalletBox />}
          {!!wallet.publicKey &&
            ((raffle.type === 'WHITELIST' || raffle.holdersOnly) &&
            !discordUsername ? (
              <>
                {raffle.type === 'WHITELIST' && (
                  <ConnectDiscordBox text='Please connect your Discord Account for whitelist raffles. This allows the raffle creator to submit your spots.' />
                )}
                {raffle.type !== 'WHITELIST' && raffle.holdersOnly && (
                  <ConnectDiscordBox text='Please connect your Discord Account for holder-only raffles. This allows us to check your holder status.' />
                )}
              </>
            ) : raffle.holdersOnly && discordUsername && !verifiedHolder ? (
              <HolderCheckBox
                communityName={raffle.creatorProject!.communityName}
                discordInviteLink={raffle.creatorProject!.discordInviteLink}
                isLoading={isVerifiedHolderLoading}
              />
            ) : raffle.needsSocial && !twitterUsername ? (
              <Stack direction={{ base: 'column', md: 'row' }} gap={3}>
                <TwitterLinkMultiButton />
              </Stack>
            ) : (
              <>
                <VStack alignItems={'center'} justifyContent='center' gap='4'>
                  <HStack
                    mt={['1rem', '1rem', 'none']}
                    width='full'
                    justifyContent='space-between'
                  >
                    <HStack width='full'>
                      <Text textAlign='right' fontWeight='bold '>
                        {previewTicketPrice ? (
                          <>
                            {formatFloatForDisplay(previewTicketPrice)}{' '}
                            {selectedPurchaseToken?.token.symbol}
                          </>
                        ) : (
                          ''
                        )}
                      </Text>
                      <Text textAlign={'left'} color='#7B7B7B'>
                        Ticket Price
                      </Text>
                    </HStack>

                    {possibleGemsEarned && possibleGemsEarned > 0 && (
                      <Box
                        color='#FFC728'
                        bg={isDarkMode ? 'primaryDark' : '#FFF8E4'}
                        h='40px'
                        w='300px'
                        border={
                          isDarkMode
                            ? 'solid 1px #FFffff50'
                            : 'solid 1px #FFDBB9'
                        }
                        borderRadius='full'
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                        fontWeight='bold'
                        gap='2'
                      >
                        <Image src='/images/rewards/GemsShine.svg' />
                        <Text color={isDarkMode ? 'white' : '#FF8F28'}>
                          <Text
                            display={['none', 'none', 'inline']}
                            color={isDarkMode ? 'white' : '#FF8F28'}
                          >
                            Buy & earn{' '}
                          </Text>
                          {formatFloatForDisplay(possibleGemsEarned)} GEMS
                        </Text>
                      </Box>
                    )}
                  </HStack>
                  <Stack
                    direction={['column', 'row']}
                    w='full'
                    justifyContent='space-between'
                    alignItems='center'
                    gap='8'
                  >
                    <Stack
                      justify='space-between'
                      align='center'
                      gap='1rem'
                      direction={['column', 'column', 'row']}
                      h='3rem !important'
                      rounded='full'
                    >
                      <Flex
                        alignItems={'center'}
                        background={
                          isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#fefefe'
                        }
                        color={isDarkMode ? '#fff' : '#232323'}
                        rounded='full'
                        border='solid 1px'
                        borderColor={isDarkMode ? '#494949' : '#E9E9E9'}
                      >
                        <Button
                          bg='transparent'
                          color={isDarkMode ? 'white' : 'black'}
                          ml='1'
                          onClick={() => {
                            const currentAmount = parseInt(
                              ticketAmountToBuy,
                              10
                            )
                            setTicketAmountToBuy(
                              (currentAmount > 1
                                ? currentAmount - 1
                                : 1
                              ).toString()
                            )
                          }}
                        >
                          -
                        </Button>
                        <Input
                          border='none'
                          color={isDarkMode ? '#fff' : '#232323'}
                          background={
                            isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#fefefe'
                          }
                          rounded='full'
                          type='number'
                          min={1}
                          max={
                            raffle.maxTicketsPerUser
                              ? raffle.maxTicketsPerUser
                              : undefined
                          }
                          maxW='200px'
                          placeholder='No of Tickets'
                          textAlign='center'
                          padding={0}
                          minW='30px'
                          value={ticketAmountToBuy}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            // Remove non-digit characters and leading zeros
                            const sanitizedValue =
                              e.target.value
                                .replace(/[^0-9]|(?<=\d{6})\d+/g, '')
                                .replace(/^0+/, '') || '0'

                            setTicketAmountToBuy(sanitizedValue)
                          }}
                        ></Input>
                        <Button
                          mr='1'
                          bg='transparent'
                          color={isDarkMode ? 'white' : 'black'}
                          onClick={() => {
                            const currentAmount = parseInt(
                              ticketAmountToBuy,
                              10
                            )
                            setTicketAmountToBuy((currentAmount + 1).toString())
                          }}
                        >
                          +
                        </Button>
                        <Stack
                          justify='right'
                          direction={['column', 'row']}
                          align='center'
                        >
                          <HStack
                            rounded='full'
                            bg='transparent'
                            color={isDarkMode ? 'white' : 'black'}
                          >
                            <Select
                              className='app-charka-react-select'
                              chakraStyles={CharkaSelectStyle(colorMode)}
                              onChange={(newVal: any, actionMeta) => {
                                console.log('selected', { newVal })

                                const purchaseToken =
                                  raffle.allowedPurchaseTokens.find(
                                    (purchaseTokenObj) =>
                                      purchaseTokenObj.token.symbol ===
                                      newVal?.value
                                  )
                                if (!purchaseToken) return
                                setSelectedPurchaseToken(purchaseToken)
                              }}
                              value={{
                                label: selectedPurchaseToken?.token.symbol,
                                value: selectedPurchaseToken?.token.symbol,
                              }}
                              options={allowedPurchaseTokens.map(
                                (purchaseTokenObj) => ({
                                  label: purchaseTokenObj.token.symbol,
                                  value: purchaseTokenObj.token.symbol,
                                })
                              )}
                            />
                          </HStack>
                        </Stack>
                      </Flex>
                    </Stack>
                    <Button
                      leftIcon={<HiOutlineTicket />}
                      variant={isDarkMode ? 'primaryDark' : 'primary'}
                      loadingText={buttonLoadingText}
                      isLoading={buyTicketRes.loading}
                      minW={['260px']}
                      w={['80%', '60%']}
                      mt='1.2rem'
                      height={['3.2rem', '3.2rem']}
                      fontSize={['0.9rem', '1rem']}
                      onClick={() => {
                        buyTicket()
                      }}
                    >
                      Buy {ticketAmountToBuy} ticket
                      {parseInt(ticketAmountToBuy, 10) === 1 ? '' : 's'}
                      {previewTicketPrice ? (
                        <>
                          {' '}
                          for{' '}
                          {formatFloatForDisplay(
                            previewTicketPrice * parseInt(ticketAmountToBuy, 10)
                          )}{' '}
                          {selectedPurchaseToken?.token.symbol}
                        </>
                      ) : (
                        ''
                      )}
                    </Button>
                  </Stack>
                </VStack>
              </>
            ))}

          {selectedPurchaseToken?.token.onDEX && (
            <Text mt='2rem' color='#7B7B7B' textAlign={'center'}>
              Need {selectedPurchaseToken?.token.symbol}? Buy on{' '}
              <Link
                isExternal
                textDecoration={'underline'}
                href={`https://jup.ag/swap/${
                  selectedPurchaseToken.token.symbol === 'SOL' ? 'USDC' : 'SOL'
                }-${selectedPurchaseToken.token.address}`}
                target='_blank'
                color={isDarkMode ? 'white' : 'black'}
              >
                Jupiter
              </Link>
            </Text>
          )}
        </>
      )}
    </Box>
  )
}
